import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { acordarFila } from './fila';
import { prepararInstalacao } from './instalar';
import { Instalar, ManifestoDoEndereco } from './telas/Instalar';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { LimiteDeErro } from './LimiteDeErro';
import { Moldura } from './Moldura';
import { espacosDaSessao, portaDaSessao } from './modulos';
import { ContaSemAcesso } from './SemAcesso';
import { ExigeSessao, ProvedorSessao, useSessao } from './sessao';
import { aplicarTema, lerTema } from './tema';
import { Adensamento } from './telas/Adensamento';
import { AdensamentoInterpretacao } from './telas/AdensamentoInterpretacao';
import { Umidade, LimiteLiquidez, LimitePlasticidade } from './telas/Triviais';
import { Estufa } from './telas/Estufa';
import { Ajustes } from './telas/Ajustes';
import { Compactacao } from './telas/Compactacao';
import { Granulometria } from './telas/Granulometria';
import { MassaEspecifica } from './telas/MassaEspecifica';
import { Classificacao } from './telas/Classificacao';
import { Mr } from './telas/Mr';
import { Triaxial } from './telas/Triaxial';
import { CisalhamentoDireto } from './telas/CisalhamentoDireto';
import { Isc } from './telas/Isc';
import { AmostraTela } from './telas/Amostra';
import { Amostras } from './telas/Amostras';
import { Descarte } from './telas/Descarte';
import { Assistente } from './telas/Assistente';
import { Entrar } from './telas/Entrar';
import { Fila } from './telas/Fila';
import { EnsaioSituacao } from './telas/EnsaioSituacao';
import { EquipamentoTela } from './telas/Equipamento';
import { PendenciasMetrologia } from './telas/PendenciasMetrologia';
import { VerificacaoTela, VerificacoesTela } from './telas/Verificacoes';
import { EquipamentoPorCodigoTela } from './telas/EquipamentoPorCodigo';
import { Equipamentos } from './telas/Equipamentos';
import { Aferir } from './telas/Aferir';
import { CategoriasDeEquipamento } from './telas/CategoriasDeEquipamento';
import { Fornecedores } from './telas/Fornecedores';
import { CampoCadastros } from './telas/Campo';
import { ProgramacaoMaquinas } from './telas/ProgramacaoMaquinas';
import { OsCronograma } from './telas/OsCronograma';
import { Competencias } from './telas/Competencias';
import {
  Treinamentos, TreinamentoNovo, TreinamentoFicha,
} from './telas/Treinamentos';
import {
  NaoConformidades, NaoConformidadeFicha,
} from './telas/NaoConformidades';
import { DiarioFicha } from './telas/Diarios';
import { Normas } from './telas/Normas';
import { Calculos } from './telas/Calculos';
import { Locais } from './telas/Locais';
import { Cadastros } from './telas/Cadastros';
import { Clientes, ClienteFicha } from './telas/Clientes';
import { OrdensDeServico } from './telas/Os';
import { OsTela } from './telas/OsFicha';
import { OsCampo } from './telas/OsCampo';
import { Campanhas } from './telas/cptu/Campanhas';
import { CampanhaTela } from './telas/cptu/CampanhaMoldura';
import { ImportarCptu } from './telas/cptu/ImportarCptu';
import { CampanhasSpt } from './telas/spt/CampanhasSpt';
import { CampanhaSpt } from './telas/spt/CampanhaSpt';
import { NovaOs } from './telas/OsNova';
import { PrimeiroAcesso } from './telas/PrimeiroAcesso';
import { Recebimento } from './telas/Recebimento';
import { ItensDeMedicao } from './telas/ItensDeMedicao';
import { Medicoes, MedicaoFicha } from './telas/Medicoes';
import { Recebimentos } from './telas/Recebimentos';
import { Painel } from './telas/Painel';
import { ParaAProgramacao, Programar } from './telas/Programar';
import { Relatos } from './telas/Relatos';
import { Padrao } from './telas/Padrao';
import { MapaDeEnsaios } from './telas/Mapa';
import { Usuarios } from './telas/Usuarios';
import { ExclusaoDefinitiva } from './telas/ExclusaoDefinitiva';
import { Exportacao } from './telas/Exportacao';
import { Relatorios } from './telas/Relatorios';
import { Assinatura, AssinaturaDaOs } from './telas/Assinatura';
import { Portal } from './telas/Portal';
// Onda 548 (Plano 2, Tarefa 4): os avisos.
import { Avisos } from './telas/Avisos';
import { Caixa } from './telas/comercial/Caixa';
import { Oportunidades } from './telas/comercial/Oportunidades';
import { Funil } from './telas/comercial/Funil';
import { Radar } from './telas/comercial/Radar';
import { PainelComercial } from './telas/comercial/PainelComercial';
import { Inicio } from './telas/Inicio';
import { Oportunidade } from './telas/comercial/Oportunidade';
import { ContatoFicha, Contatos } from './telas/comercial/Contatos';
import { Tarefas } from './telas/comercial/Tarefas';
import { AjustesComercial } from './telas/comercial/AjustesComercial';
import { Agente } from './telas/comercial/Agente';
import './estilo.css';
import './celular.css';

/**
 * As rotas que existem hoje: recebimento de amostras e equipamentos.
 *
 * O menu lateral mostra o mapa inteiro do sistema, com as seções ainda não
 * construídas apagadas e etiquetadas — quem manda nisso é a `Moldura`. Aqui
 * ficam só as rotas de verdade: caminho sem tela cai no `*` e volta para a
 * lista de recebimentos, nunca numa página em branco.
 *
 * Equipamentos entrou antes dos ensaios porque é pré-requisito deles: a
 * `RN-151` não deixa ensaio nenhum escolher equipamento sem aferição
 * válida, e o número que o cálculo usa como divisor — massa, volume,
 * altura — sai da aferição, não do catálogo.
 */

// Antes do primeiro quadro: sem isto a tela pisca clara e depois escurece.
aplicarTema(lerTema());
// O manifesto do aplicativo certo e o pedido de instalação (onda 298 — o
// app no celular). Antes do roteador: o ícone abre com `?origem=app-…`, e a
// consulta sai do endereço antes de alguém guardá-lo.
prepararInstalacao();

const comMoldura = (tela: React.ReactNode) => (
  <ExigeSessao><Moldura>{tela}</Moldura></ExigeSessao>
);

/**
 * A porta de entrada, por ESPAÇO (onda 295, spec 03 §11, critérios 1 a 3).
 *
 * Até a H1 a porta era por perfil (UX-015): quem via indicadores entrava no
 * Painel, quem não via entrava pela fila, pelas amostras ou pela OS — para o
 * auxiliar não começar o dia por uma recusa. A regra continua, um nível
 * acima: a porta é a do espaço que a pessoa alcança (`portaDaSessao`). Quem
 * tem mais de um entra no Hub, na lista de OS (D8 — o Painel geral do Hub é
 * da H2); o laboratorista entra na fila; o sondador, no Campo. Dentro de cada
 * espaço a ordem de preferência é a de `rotaDoEspaco` — a do LabManager é a
 * da porta antiga.
 *
 * O espaço vai junto na navegação (`state.espaco`): a lista de OS é do Hub E
 * do LabManager, e a aba que lembrava o LabManager (o sessionStorage da H0)
 * abriria a lista no LabManager. Com o espaço na navegação, a moldura abre no
 * Hub já no primeiro quadro (ponto de atenção 1).
 *
 * A conta que não alcança espaço nenhum não tem porta: fica em `/`, com a
 * página que diz isso, e a engrenagem continua ao lado do nome.
 *
 * O link antigo do Painel: até a H1 o Painel morava em `/`, e um `/?aba=geral`
 * guardado (ou mandado por e-mail) passaria pela porta e levaria ao Hub quem
 * tem vários espaços. Com `?aba=` no endereço e o Painel ao alcance, vai ao
 * Painel, com a mesma busca, no LabManager (o espaço dele).
 */
function Porta() {
  const { eu, pode } = useSessao();
  const { search } = useLocation();
  if (new URLSearchParams(search).has('aba') && pode('indicador.ver')) {
    return <Navigate to={`/painel${search}`} replace state={{ espaco: 'labmanager' }} />;
  }
  const porta = portaDaSessao(espacosDaSessao(eu?.espacos ?? []), eu?.permissoes ?? []);
  if (!porta) return <ContaSemAcesso />;
  return <Navigate to={porta.rota} replace state={{ espaco: porta.espaco }} />;
}

/*
 * O trabalhador de serviço, e a fila que ele torna útil (onda 127).
 *
 * A ordem é esta: registrar o trabalhador para a casca abrir sem rede, e
 * ACORDAR a fila para o que ficou guardado do dia anterior subir sozinho.
 * Sem o primeiro, o técnico não abre a tela na barragem; sem o segundo,
 * ele teria de lembrar de tocar em alguma coisa para os lançamentos
 * saírem — e o que depende de lembrar é o que fica no aparelho.
 *
 * `catch` em silêncio nos dois: navegador sem trabalhador de serviço
 * (janela privada, http em rede interna) continua funcionando, só sem o
 * off-line. Um erro na inicialização por causa disso derrubaria a tela
 * INTEIRA para quem está com rede e não precisa de nada disso.
 */
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => { /* sem PWA */ });
  });
}
void acordarFila();

/*
 * A rede de baixo para a exceção que ninguém pegou (03/09/2026).
 *
 * Um clique cuja promessa rejeita sem `catch` não faz NADA na tela — o
 * botão parece morto, e a pessoa relata «não consigo registrar». O
 * `LimiteDeErro` pega o que quebra ao desenhar; isto pega o resto: a
 * rejeição solta e o erro solto. Mostra uma faixa com a frase, para o
 * relato vir com ela em vez de vir com «não acontece nada».
 */
function faixaDeFalha(texto: string) {
  try {
    let el = document.getElementById('falha-silenciosa');
    if (!el) {
      el = document.createElement('div');
      el.id = 'falha-silenciosa';
      el.setAttribute('role', 'alert');
      el.style.cssText = 'position:fixed;left:50%;bottom:1rem;transform:translateX(-50%);'
        + 'max-width:min(40rem,92vw);z-index:9999;background:#7a1f1f;color:#fff;'
        + 'padding:.7rem 2.4rem .7rem 1rem;border-radius:.5rem;font:14px system-ui,sans-serif;'
        + 'box-shadow:0 6px 24px rgba(0,0,0,.35)';
      const fechar = document.createElement('button');
      fechar.textContent = '×';
      fechar.setAttribute('aria-label', 'Fechar');
      fechar.style.cssText = 'position:absolute;right:.4rem;top:.3rem;background:none;'
        + 'border:0;color:#fff;font-size:1.3rem;cursor:pointer';
      fechar.onclick = () => el?.remove();
      el.appendChild(document.createElement('span'));
      el.appendChild(fechar);
      document.body.appendChild(el);
    }
    (el.firstChild as HTMLElement).textContent =
      `Algo falhou nesta tela e a ação não foi concluída: ${texto}`;
  } catch { /* nada */ }
}
window.addEventListener('unhandledrejection', (ev) => {
  const r = ev.reason as { message?: string } | undefined;
  const texto = r?.message ?? String(ev.reason ?? 'erro desconhecido');
  /* Cancelamento de requisição não é falha: a tela mudou de rota. */
  if (/AbortError|aborted/i.test(texto)) return;
  console.error('[falha silenciosa]', ev.reason);
  faixaDeFalha(texto);
});
window.addEventListener('error', (ev) => {
  if (!ev.message) return;
  console.error('[falha silenciosa]', ev.error ?? ev.message);
  faixaDeFalha(ev.message);
});

/*
 * O cache das leituras que se repetem sozinhas (onda 308). O primeiro uso é o
 * número da Estufa na barra do celular (`contagemDaEstufa.ts`), relido a cada
 * minuto; quem grava uma pesagem pode invalidar a chave dele por aqui. As telas
 * continuam lendo com `api()` como sempre — isto não é uma troca de padrão.
 */
const consultas = new QueryClient();

createRoot(document.getElementById('raiz')!).render(
  <StrictMode>
    <QueryClientProvider client={consultas}>
    <BrowserRouter>
      <ManifestoDoEndereco />
      <ProvedorSessao>
        {/*
          * O limite fica DENTRO do provedor de sessao e FORA das rotas: uma
          * tela que quebra nao derruba a sessao, e a pessoa nao e jogada
          * para o login com o trabalho pela metade. Ver `LimiteDeErro`.
          */}
        <LimiteDeErro>
        <Routes>
          <Route path="/entrar" element={<Entrar />} />
          {/*
            * 298 · o app no celular. Pública, como a entrada: é o endereço
            * que se manda pelo WhatsApp (`/instalar/campo`), e quem o abre
            * pode ainda não ter entrado.
            */}
          <Route path="/instalar/:app?" element={<Instalar />} />
          {/*
            * Pública, como a de entrada: quem chega aqui ainda não tem
            * conta ativa. Fica FORA de `ExigeSessao` de propósito — se
            * estivesse dentro, o convite mandaria a pessoa para a tela
            * de login, que é exatamente onde ela não consegue entrar.
            */}
          <Route path="/primeiro-acesso/:token" element={<PrimeiroAcesso />} />
          {/*
            O Painel foi a porta de entrada de 23/08/2026 até a H1 (onda
            295). Até 23/08 `/` caía no Recebimento — herança de quando ele
            era a única tela que existia —, e a pergunta de quem abre o
            sistema pela manhã é "o que precisa de mim hoje". Desde a H1 `/`
            é a porta do ESPAÇO (`Porta`, acima): o Painel é do LabManager,
            e quem tem mais de um espaço entra no Hub. O Painel mora em
            `/painel`, que é o endereço do menu e o que se compartilha.
          */}
          <Route path="/" element={comMoldura(<Porta />)} />
          <Route path="/painel" element={comMoldura(<Painel />)} />
          {/* 550 · o Início do Hub, «O que precisa de você hoje» (Tarefa 20): a porta do Hub. */}
          <Route path="/inicio" element={comMoldura(<Inicio />)} />
          {/*
            * `/os/nova` antes de `/os/:id` por leitura, não por
            * necessidade: o roteador já dá preferência ao trecho fixo
            * sobre o dinâmico. Quem lê a lista de rotas não deveria ter
            * que saber disso para confiar no que está escrito.
            */}
          <Route path="/os" element={comMoldura(<OrdensDeServico />)} />
          <Route path="/os/nova" element={comMoldura(<NovaOs />)} />
          <Route path="/os/:id" element={comMoldura(<OsTela />)} />
          {/* 256 · a OS em abas: cada aba com endereço próprio. */}
          <Route path="/os/:id/escopo" element={comMoldura(<OsTela aba="escopo" />)} />
          <Route path="/os/:id/amostras" element={comMoldura(<OsTela aba="amostras" />)} />
          <Route path="/os/:id/quantitativos" element={comMoldura(<OsTela aba="quantitativos" />)} />
          <Route path="/os/:id/historico" element={comMoldura(<OsTela aba="historico" />)} />
          {/*
            * O mapa dos ensaios é um item próprio (onda 177): «o que já
            * se ensaiou por aqui?». `/mapa` é o endereço que se
            * compartilha — com os filtros dentro dele. O endereço da
            * onda 171 (`/relatorios/mapa`) viveu umas horas em
            * homologação e não deixou link guardado; não fica redirect,
            * porque o teste 18.4 exige que toda rota tenha módulo dono,
            * e Relatórios voltou a não ter tela.
            */}
          <Route path="/mapa" element={comMoldura(<MapaDeEnsaios />)} />
          <Route path="/relatorios" element={comMoldura(<Relatorios />)} />
          {/*
            * O portal do cliente, do lado de DENTRO (0172).
            *
            * O que o cliente acessa não está aqui: é outra aplicação, em
            * portal.testesolo.com.br, com processo e usuário de banco
            * próprios. Esta rota é a tela da Testesolo para decidir o que
            * vai para lá e para ver quem abriu.
            */}
          <Route path="/portal" element={comMoldura(<Portal />)} />
          <Route path="/medicoes" element={comMoldura(<Medicoes />)} />
          <Route path="/itens-medicao" element={comMoldura(<ItensDeMedicao />)} />
          <Route path="/medicao/:id" element={comMoldura(<MedicaoFicha />)} />
          <Route path="/recebimentos" element={comMoldura(<Recebimentos />)} />
          <Route path="/recebimento/novo" element={comMoldura(<Assistente />)} />
          <Route path="/recebimento/:id" element={comMoldura(<Recebimento />)} />
          <Route path="/amostras" element={comMoldura(<Amostras />)} />
          <Route path="/fila" element={comMoldura(<Fila />)} />
          <Route path="/estufa" element={comMoldura(<Estufa />)} />
          <Route path="/ajustes" element={comMoldura(<Ajustes />)} />
          <Route
            path="/ensaio/:id/situacao"
            element={comMoldura(<EnsaioSituacao />)}
          />
          {/*
            * A programação é DA OS: a rota carrega o id dela.
            *
            * A rota antiga `/programar` (sem OS) some junto com o item de
            * menu — ver o comentário em `modulos.ts`. Quem tiver o
            * endereço antigo salvo cai na lista de OS, que é o lugar de
            * onde se escolhe.
            */}
          {/* 257 · a programação é uma aba da OS; o endereço antigo leva a ela. */}
          <Route path="/os/:id/programacao" element={comMoldura(<Programar />)} />
          <Route path="/os/:id/programar" element={<ParaAProgramacao />} />
          {/* 304 (RN-249) · o cronograma da OS, logo depois da Programação: só leitura. */}
          <Route path="/os/:id/cronograma" element={comMoldura(<OsCronograma />)} />
          {/* Furos, pontos e diários da OS (204) — saíram da ficha. */}
          <Route path="/os/:id/campo" element={comMoldura(<OsCampo />)} />
          {/*
            * 312 (RN-253) · a campanha CPTu: a lista, a casca com as abas
            * (cada aba com endereço próprio, como a OS) e a importação.
            */}
          <Route path="/cptu" element={comMoldura(<Campanhas />)} />
          <Route path="/cptu/:id" element={comMoldura(<CampanhaTela aba="verticais" />)} />
          {/* A vertical aberta é sub-rota da aba Verticais (onda 313, R3): Perfis, Tabela, Edição e Camadas (313.6). */}
          <Route path="/cptu/:id/verticais/:vid" element={comMoldura(<CampanhaTela aba="verticais" vista="perfis" />)} />
          <Route path="/cptu/:id/verticais/:vid/tabela" element={comMoldura(<CampanhaTela aba="verticais" vista="tabela" />)} />
          <Route path="/cptu/:id/verticais/:vid/edicao" element={comMoldura(<CampanhaTela aba="verticais" vista="edicao" />)} />
          <Route path="/cptu/:id/verticais/:vid/camadas" element={comMoldura(<CampanhaTela aba="verticais" vista="camadas" />)} />
          <Route path="/cptu/:id/dissipacoes" element={comMoldura(<CampanhaTela aba="dissipacoes" />)} />
          {/* A dissipação aberta é sub-rota da aba Dissipações, e a aba Premissas tem endereço próprio (313.5, R3). */}
          <Route path="/cptu/:id/dissipacoes/:did" element={comMoldura(<CampanhaTela aba="dissipacoes" />)} />
          <Route path="/cptu/:id/premissas" element={comMoldura(<CampanhaTela aba="premissas" />)} />
          <Route path="/cptu/:id/analise" element={comMoldura(<CampanhaTela aba="analise" vistaDaAnalise="mapa" />)} />
          <Route path="/cptu/:id/analise/perfis" element={comMoldura(<CampanhaTela aba="analise" vistaDaAnalise="perfis" />)} />
          <Route path="/cptu/:id/analise/3d" element={comMoldura(<CampanhaTela aba="analise" vistaDaAnalise="3d" />)} />
          <Route path="/cptu/:id/relatorio" element={comMoldura(<CampanhaTela aba="relatorio" />)} />
          <Route path="/cptu/:id/historico" element={comMoldura(<CampanhaTela aba="historico" />)} />
          <Route path="/cptu/:id/importar" element={comMoldura(<ImportarCptu />)} />
          <Route path="/spt" element={comMoldura(<CampanhasSpt />)} />
          <Route path="/spt/:id" element={comMoldura(<CampanhaSpt />)} />
          <Route path="/spt/:id/furos/:fid" element={comMoldura(<CampanhaSpt />)} />
          <Route path="/spt/:id/relatorio" element={comMoldura(<CampanhaSpt />)} />
          <Route path="/spt/:id/edicao" element={comMoldura(<CampanhaSpt />)} />
          <Route path="/programar" element={<Navigate to="/os" replace />} />
          {/*
            * A interpretação ANTES do lançamento na lista, por leitura.
            * O roteador já prefere o trecho fixo ao dinâmico, mas aqui
            * nem há disputa: são dois caminhos distintos. Ficam juntos
            * porque são a mesma tela partida em duas (bancada × mesa), e
            * quem ler a lista precisa ver isso.
            */}
          <Route
            path="/ensaio/:id/adensamento/interpretacao"
            element={comMoldura(<AdensamentoInterpretacao />)}
          />
          <Route
            path="/ensaio/:id/adensamento"
            element={comMoldura(<Adensamento />)}
          />
          <Route
            path="/ensaio/:id/umidade"
            element={comMoldura(<Umidade />)}
          />
          <Route
            path="/ensaio/:id/limite-liquidez"
            element={comMoldura(<LimiteLiquidez />)}
          />
          <Route
            path="/ensaio/:id/limite-plasticidade"
            element={comMoldura(<LimitePlasticidade />)}
          />
          <Route
            path="/ensaio/:id/compactacao"
            element={comMoldura(<Compactacao />)}
          />
          <Route
            path="/ensaio/:id/granulometria"
            element={comMoldura(<Granulometria />)}
          />
          <Route
            path="/ensaio/:id/massa-especifica"
            element={comMoldura(<MassaEspecifica />)}
          />
          <Route
            path="/ensaio/:id/classificacao"
            element={comMoldura(<Classificacao />)}
          />
          <Route
            path="/ensaio/:id/isc"
            element={comMoldura(<Isc />)}
          />
          <Route
            path="/ensaio/:id/mr"
            element={comMoldura(<Mr />)}
          />
          <Route
            path="/ensaio/:id/triaxial"
            element={comMoldura(<Triaxial />)}
          />
          <Route
            path="/ensaio/:id/cisalhamento-direto"
            element={comMoldura(<CisalhamentoDireto />)}
          />
          <Route path="/amostra/:codigo" element={comMoldura(<AmostraTela />)} />
          <Route path="/descarte" element={comMoldura(<Descarte />)} />
          <Route path="/equipamentos" element={comMoldura(<Equipamentos />)} />
          {/*
            * Padrões deixou de ser item de menu (onda 208): é a aba
            * «Padrões» dentro de Equipamentos e calibrações. A rota fica,
            * para o favorito e o link antigo continuarem chegando.
            */}
          <Route
            path="/padroes"
            element={<Navigate to="/equipamentos?familia=padrao" replace />}
          />
          {/*
            * ANTES de `/equipamento/:id`: `codigo` casaria com `:id` e a
            * ficha tentaria carregar «codigo» como se fosse um uuid.
            */}
          <Route
            path="/equipamento/codigo/:codigo"
            element={comMoldura(<EquipamentoPorCodigoTela />)}
          />
          {/*
            * ANTES de `/equipamento/:id`, e sob `/equipamentos/…` de
            * propósito: a aferição interna é do módulo de equipamentos, e
            * o registro de módulos reivindica tudo que começa com
            * `/equipamento` (teste 18.4).
            */}
          <Route
            path="/equipamentos/pendencias"
            element={comMoldura(<PendenciasMetrologia />)}
          />
          <Route
            path="/equipamentos/aferir-massas"
            element={comMoldura(<Aferir />)}
          />
          <Route
            path="/equipamentos/verificacoes"
            element={comMoldura(<VerificacoesTela />)}
          />
          <Route
            path="/equipamentos/verificacoes/:id"
            element={comMoldura(<VerificacaoTela />)}
          />
          <Route path="/equipamento/:id" element={comMoldura(<EquipamentoTela />)} />
          <Route
            path="/categorias-de-equipamento"
            element={comMoldura(<CategoriasDeEquipamento />)}
          />
          <Route path="/fornecedores" element={comMoldura(<Fornecedores />)} />
          <Route path="/campo" element={comMoldura(<CampoCadastros />)} />
          {/*
            * O cronograma é UM (onda 200). A linha do tempo por máquina
            * (0152) era rota irmã de uma tela por atividade, e o menu
            * abria a irmã que ninguém usava: «quando clico em Cronograma
            * aparece um cronograma antigo que não será usado» (Humberto,
            * 22/09). A tela antiga saiu; o endereço antigo redireciona,
            * porque há link guardado em favorito e em doc.
            */}
          <Route path="/cronograma" element={comMoldura(<ProgramacaoMaquinas />)} />
          <Route path="/cronograma/maquinas"
            element={<Navigate to="/cronograma" replace />} />
          <Route path="/competencias" element={comMoldura(<Competencias />)} />
          <Route path="/treinamentos" element={comMoldura(<Treinamentos />)} />
          <Route path="/treinamentos/novo"
            element={comMoldura(<TreinamentoNovo />)} />
          <Route path="/treinamentos/:id"
            element={comMoldura(<TreinamentoFicha />)} />
          <Route path="/nao-conformidades"
            element={comMoldura(<NaoConformidades />)} />
          <Route path="/nao-conformidades/:id"
            element={comMoldura(<NaoConformidadeFicha />)} />
          <Route path="/campo/diario/:id" element={comMoldura(<DiarioFicha />)} />
          <Route path="/normas" element={comMoldura(<Normas />)} />
          <Route path="/calculos" element={comMoldura(<Calculos />)} />
          <Route path="/locais" element={comMoldura(<Locais />)} />
          {/*
            * A porta dos cadastros (onda 175, 08/09/2026). Locais e
            * fornecedores continuam nos endereços de sempre — quem tem
            * o link salvo não recebe 404 —, mas saíram do menu e se
            * chega a eles por aqui. Clientes é tela nova.
            */}
          <Route path="/cadastros" element={comMoldura(<Cadastros />)} />
          <Route path="/clientes" element={comMoldura(<Clientes />)} />
          <Route path="/cliente/:id" element={comMoldura(<ClienteFicha />)} />
          <Route path="/usuarios" element={comMoldura(<Usuarios />)} />
          <Route path="/exportacao" element={comMoldura(<Exportacao />)} />
          <Route path="/exclusao-definitiva" element={comMoldura(<ExclusaoDefinitiva />)} />
          <Route path="/assinatura" element={comMoldura(<Assinatura />)} />
          <Route path="/assinatura/:numero"
            element={comMoldura(<AssinaturaDaOs />)} />
          <Route path="/relatos" element={comMoldura(<Relatos />)} />
          {/* 548 · os avisos da pessoa: o sino da moldura leva aqui. */}
          <Route path="/avisos" element={comMoldura(<Avisos />)} />
          <Route path="/padrao" element={comMoldura(<Padrao />)} />
          {/* 550 · o Comercial: a caixa de entrada; a conversa aberta é a mesma tela. */}
          <Route path="/comercial/caixa" element={comMoldura(<Caixa />)} />
          <Route path="/comercial/conversa/:id" element={comMoldura(<Caixa />)} />
          <Route path="/comercial/oportunidades" element={comMoldura(<Oportunidades />)} />
          <Route path="/comercial/oportunidade/:id" element={comMoldura(<Oportunidade />)} />
          {/* 550 · o quadro do funil (Tarefa 18). */}
          <Route path="/comercial/funil" element={comMoldura(<Funil />)} />
          {/* 550 · o Radar (Tarefa 19). */}
          <Route path="/comercial/radar" element={comMoldura(<Radar />)} />
          {/* 552 · o painel do Comercial (Tarefa 28). */}
          <Route path="/comercial/painel" element={comMoldura(<PainelComercial />)} />
          <Route path="/comercial/contatos" element={comMoldura(<Contatos />)} />
          <Route path="/comercial/contato/:id" element={comMoldura(<ContatoFicha />)} />
          <Route path="/comercial/tarefas" element={comMoldura(<Tarefas />)} />
          <Route path="/comercial/ajustes" element={comMoldura(<AjustesComercial />)} />
          <Route path="/comercial/agente" element={comMoldura(<Agente />)} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </LimiteDeErro>
      </ProvedorSessao>
    </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
